import {
  FC,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { debounce } from "lodash";
import __ from "src/utils/Translations";
import useFetchAreasData from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchAreasData";
import { useParams } from "react-router-dom";
import useFetchStructureData from "src/Pages/Company/OrganizationStructure/api/useFetchStructureData";
import useFetchAvailableParents from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchAvailableParents";

interface IAutocompleteMainArea {
  companyId: string;
  structureId: string;
  areaId?: string;
  isEdit?: boolean;
}

const AutocompleteMainArea: FC<IAutocompleteMainArea> = ({
  companyId,
  structureId,
  areaId,
  isEdit = false,
}) => {
  const { control } = useFormContext();
  const [search, setSearch] = useState("");

  const {
    data: areaData,
    fetchNextPage: areaFetchNextPage,
    hasNextPage: areaHasNextPage,
    isFetching: areaIsFetching,
  } = useFetchAreasData(companyId, structureId, search, isEdit);

  const {
    data: editAreaData,
    fetchNextPage: editAreaFetchNextPage,
    hasNextPage: editAreaHasNextPage,
    isFetching: editAreaIsFetching,
  } = useFetchAvailableParents(companyId, structureId, areaId, search);

  const getRelevantProperty = isEdit
    ? {
        data: editAreaData,
        fetchNextPage: editAreaFetchNextPage,
        hasNextPage: editAreaHasNextPage,
        isFetching: editAreaIsFetching,
      }
    : {
        data: areaData,
        fetchNextPage: areaFetchNextPage,
        hasNextPage: areaHasNextPage,
        isFetching: areaIsFetching,
      };

  const { data, fetchNextPage, hasNextPage, isFetching } = getRelevantProperty;

  const { areaId: excludedValue } = useParams<{ areaId: string }>();
  const { data: structureData, isFetching: structureDataIsFetching } =
    useFetchStructureData(companyId, structureId);

  const options = useMemo(() => {
    if (!structureData) return [];

    return [
      { id: null, label: structureData.name },
      ...(data?.pages?.flatMap((page) => page.content) || []),
    ].filter((option) => option.id !== excludedValue);
  }, [data, excludedValue, structureData]);

  const debouncedSearch = useRef(
    debounce((_: SyntheticEvent, value: string) => {
      setSearch(value);
    }, 300)
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleScroll = (event: SyntheticEvent) => {
    const listbox = event.currentTarget;
    if (
      (listbox.scrollTop + listbox.clientHeight) / listbox.scrollHeight > 0.9 &&
      hasNextPage &&
      !isFetching
    ) {
      fetchNextPage();
    }
  };

  if (structureDataIsFetching) {
    return <Skeleton width="100%" height={56} />;
  }

  return (
    <Controller
      name="parent"
      control={control}
      render={({ field: { onChange, value }, fieldState }) => (
        <Autocomplete
          options={options}
          disableListWrap
          disablePortal
          value={value}
          onChange={(_, newValue) => onChange(newValue)}
          onInputChange={(event, newInputValue, reason) => {
            if (reason !== "reset") {
              debouncedSearch(event, newInputValue);
            }
          }}
          onOpen={() => setSearch("")}
          noOptionsText={__("Brak wyników wyszukiwania")}
          getOptionLabel={(option) =>
            typeof option === "string"
              ? option
              : option?.label ?? structureData?.name
          }
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={__("Nadrzędny obszar")}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isFetching && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          slotProps={{
            listbox: {
              onScroll: handleScroll,
              style: { maxHeight: "300px", overflowY: "auto" },
            },
          }}
        />
      )}
    />
  );
};

export default AutocompleteMainArea;
